/** Supplier Onboarding wizard (Demo Mode only, local-only - no backend persistence yet).
 * Modeled on OFD's retailer onboarding wizard pattern (step-by-step progression with a
 * completion gate per step) - see
 * src/pages/orderly-for-distributors/Onboarding/index.tsx for the original pattern, and on
 * Customer Management's onboarding journey rework (tiered path by record type).
 *
 * Draws from the Customer Management onboarding model but tiered by supplier type (Full /
 * Approved local / One-off / Micro / Trial): every type captures the same basic details set,
 * only "Full supplier" additionally walks through the existing Due Diligence and Delivery &
 * Pricing governance sections (see ../components/governance/) - reused, not duplicated. An
 * escalation-criteria check (spend/multi-site/high-risk/recurring/client evidence/ESG) forces
 * Full onboarding regardless of the type initially selected. Replaces the previous plain
 * "quick onboarding" switch. SupplierForm and its Governance tab are unchanged and remain the
 * primary supplier CRUD screen. */

import { useContext, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  MenuItem,
  Step,
  StepLabel,
  Stepper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import PageArea from '../../../../components/shared/PageArea';
import PageTitle from '../../../../components/shared/PageTitle';
import BackButton from '../../../../components/shared/actions/BackButton';
import { Section } from '../../../../components/ui/Section';
import { UserContext } from '../../../../components/shared/useUser';
import {
  createQuickOnboardGovernanceProfile,
  EMPTY_SUPPLIER_FULL_ONBOARDING_TRIGGER_CRITERIA,
  EMPTY_SUPPLIER_ONBOARDING_BASIC_DETAILS,
  evaluateFullOnboardingTriggers,
  getSupplierGovernanceProfile,
  SUPPLIER_ALLERGEN_RESPONSIBILITY_LABELS,
  SUPPLIER_DELIVERY_METHOD_LABELS,
  SUPPLIER_ONBOARDING_TYPE_DESCRIPTIONS,
  SUPPLIER_ONBOARDING_TYPE_INCLUDES_FULL_GOVERNANCE,
  SUPPLIER_ONBOARDING_TYPE_LABELS,
  SUPPLIER_ONBOARDING_TYPE_ORDER,
  SUPPLIER_PAYMENT_INVOICE_METHOD_LABELS,
  SupplierAllergenResponsibility,
  SupplierApprovalStatus,
  SupplierDeliveryMethod,
  SupplierFullOnboardingTriggerCriteria,
  SupplierOnboardingType,
  SupplierPaymentInvoiceMethod,
} from '../../../../mocks/data/supplierGovernanceData';
import DueDiligenceSection from '../components/governance/DueDiligenceSection';
import DeliveryAndPricingSection from '../components/governance/DeliveryAndPricingSection';

enum SupplierOnboardingStep {
  BasicDetails = 0,
  EscalationCheck = 1,
  DueDiligence = 2,
  DeliveryAndPricing = 3,
  Review = 4,
}

const SupplierOnboardingWizard = () => {
  const { t } = useTranslation('suppliers');
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const { hasPermissionTo } = useContext(UserContext);
  const supplierId = id ? Number(id) : 0;
  const canApprove = hasPermissionTo(['SupplierGovernanceApprove']);

  const [selectedType, setSelectedType] = useState<SupplierOnboardingType>(SupplierOnboardingType.Trial);
  const [triggerCriteria, setTriggerCriteria] = useState<SupplierFullOnboardingTriggerCriteria>(
    EMPTY_SUPPLIER_FULL_ONBOARDING_TRIGGER_CRITERIA,
  );
  const [activeStep, setActiveStep] = useState<SupplierOnboardingStep>(SupplierOnboardingStep.BasicDetails);
  const [basicDetails, setBasicDetails] = useState(EMPTY_SUPPLIER_ONBOARDING_BASIC_DETAILS);
  const [approved, setApproved] = useState(false);

  const triggerReasons = useMemo(() => evaluateFullOnboardingTriggers(triggerCriteria), [triggerCriteria]);
  const fullOnboardingRequired = triggerReasons.length > 0;
  const effectiveType = fullOnboardingRequired ? SupplierOnboardingType.Full : selectedType;
  const quickOnboard = !SUPPLIER_ONBOARDING_TYPE_INCLUDES_FULL_GOVERNANCE[effectiveType];

  const profile = useMemo(
    () => (quickOnboard ? createQuickOnboardGovernanceProfile(supplierId) : getSupplierGovernanceProfile(supplierId)),
    [quickOnboard, supplierId],
  );

  const steps = useMemo(() => (
    SUPPLIER_ONBOARDING_TYPE_INCLUDES_FULL_GOVERNANCE[effectiveType]
      ? [
        SupplierOnboardingStep.BasicDetails,
        SupplierOnboardingStep.EscalationCheck,
        SupplierOnboardingStep.DueDiligence,
        SupplierOnboardingStep.DeliveryAndPricing,
        SupplierOnboardingStep.Review,
      ]
      : [SupplierOnboardingStep.BasicDetails, SupplierOnboardingStep.EscalationCheck, SupplierOnboardingStep.Review]
  ), [effectiveType]);

  const stepLabels: Record<SupplierOnboardingStep, string> = {
    [SupplierOnboardingStep.BasicDetails]: t('onboardingStepBasicDetails', 'Supplier & Contact Details'),
    [SupplierOnboardingStep.EscalationCheck]: t('onboardingStepEscalationCheck', 'Full Onboarding Check'),
    [SupplierOnboardingStep.DueDiligence]: t('onboardingStepDueDiligence', 'Due Diligence'),
    [SupplierOnboardingStep.DeliveryAndPricing]: t('onboardingStepDeliveryAndPricing', 'Delivery & Pricing'),
    [SupplierOnboardingStep.Review]: t('onboardingStepReview', 'Review & Approve'),
  };

  const isBasicDetailsComplete = basicDetails.supplierName.trim() !== ''
    && basicDetails.contactName.trim() !== ''
    && basicDetails.contactEmail.trim() !== ''
    && basicDetails.approvalOwner.trim() !== ''
    && basicDetails.expiryReviewDate.trim() !== '';
  const currentStepIndex = steps.indexOf(activeStep);
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === steps.length - 1;

  const updateBasicDetails = (patch: Partial<typeof basicDetails>) => setBasicDetails((prev) => ({ ...prev, ...patch }));
  const updateTriggerCriteria = (patch: Partial<SupplierFullOnboardingTriggerCriteria>) => setTriggerCriteria((prev) => ({ ...prev, ...patch }));

  const handleNext = () => {
    if (!isLastStep) setActiveStep(steps[currentStepIndex + 1]);
  };

  const handleBack = () => {
    if (!isFirstStep) setActiveStep(steps[currentStepIndex - 1]);
  };

  const handleComplete = () => {
    setApproved(true);
  };

  return (
    <PageArea>
      <Grid item
        mb={6}
        sx={{ textAlign: 'left' }}>
        <BackButton
          handleClick={() => navigate('/procurement/suppliers')}
          title={t('backToSuppliers', 'Back to Suppliers')}
          isLink={false}
        />
      </Grid>

      <PageTitle>{t('onboardingWizardTitle', 'Supplier Onboarding')}</PageTitle>

      <Box sx={{ mt: 4, mb: 4 }}>
        <TextField
          select
          label={t('supplierType', 'Supplier type')}
          value={selectedType}
          disabled={fullOnboardingRequired}
          onChange={(e) => {
            setSelectedType(e.target.value as SupplierOnboardingType);
            setActiveStep(SupplierOnboardingStep.BasicDetails);
          }}
          helperText={SUPPLIER_ONBOARDING_TYPE_DESCRIPTIONS[effectiveType]}
          sx={{ minWidth: 360 }}
        >
          {SUPPLIER_ONBOARDING_TYPE_ORDER.map((type) => (
            <MenuItem key={type}
              value={type}>
              {SUPPLIER_ONBOARDING_TYPE_LABELS[type]}
            </MenuItem>
          ))}
        </TextField>

        {fullOnboardingRequired && (
          <Alert severity="warning"
            sx={{ mt: 3 }}>
            {t('onboardingEscalatedToFull', 'Full onboarding is required for this supplier based on the checks below - the supplier type has been switched to "Full supplier".')}
          </Alert>
        )}
      </Box>

      <Stepper activeStep={currentStepIndex}
        sx={{ mb: 6 }}>
        {steps.map((step) => (
          <Step key={step}>
            <StepLabel>{stepLabels[step]}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {activeStep === SupplierOnboardingStep.BasicDetails && (
        <Section>
          <Box sx={{ p: 4 }}>
            <Typography variant="textSM"
              fontWeight={600}
              sx={{ mb: 3 }}>
              {t('onboardingStepBasicDetails', 'Supplier & Contact Details')}
            </Typography>
            <Grid container
              spacing={4}>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('supplierName', 'Supplier name')}
                  value={basicDetails.supplierName}
                  onChange={(e) => updateBasicDetails({ supplierName: e.target.value })}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('contactName', 'Contact')}
                  value={basicDetails.contactName}
                  onChange={(e) => updateBasicDetails({ contactName: e.target.value })}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('contactEmail', 'Contact email')}
                  value={basicDetails.contactEmail}
                  onChange={(e) => updateBasicDetails({ contactEmail: e.target.value })}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('contactPhone', 'Contact phone')}
                  value={basicDetails.contactPhone}
                  onChange={(e) => updateBasicDetails({ contactPhone: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('siteLocationsServed', 'Site/location served')}
                  value={basicDetails.siteLocationsServed}
                  onChange={(e) => updateBasicDetails({ siteLocationsServed: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('productRangeSupplied', 'Product/range supplied')}
                  value={basicDetails.productRangeSupplied}
                  onChange={(e) => updateBasicDetails({ productRangeSupplied: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('approvalOwner', 'Basic approval owner')}
                  value={basicDetails.approvalOwner}
                  onChange={(e) => updateBasicDetails({ approvalOwner: e.target.value })}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  select
                  label={t('allergenResponsibility', 'Allergen responsibility')}
                  value={basicDetails.allergenResponsibility}
                  onChange={(e) => updateBasicDetails({ allergenResponsibility: e.target.value as SupplierAllergenResponsibility })}
                  fullWidth
                >
                  {(Object.keys(SUPPLIER_ALLERGEN_RESPONSIBILITY_LABELS) as SupplierAllergenResponsibility[]).map((option) => (
                    <MenuItem key={option}
                      value={option}>
                      {SUPPLIER_ALLERGEN_RESPONSIBILITY_LABELS[option]}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  select
                  label={t('paymentInvoiceMethod', 'Payment/invoice method')}
                  value={basicDetails.paymentInvoiceMethod}
                  onChange={(e) => updateBasicDetails({ paymentInvoiceMethod: e.target.value as SupplierPaymentInvoiceMethod })}
                  fullWidth
                >
                  {(Object.keys(SUPPLIER_PAYMENT_INVOICE_METHOD_LABELS) as SupplierPaymentInvoiceMethod[]).map((option) => (
                    <MenuItem key={option}
                      value={option}>
                      {SUPPLIER_PAYMENT_INVOICE_METHOD_LABELS[option]}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  select
                  label={t('deliveryMethod', 'Delivery method')}
                  value={basicDetails.deliveryMethod}
                  onChange={(e) => updateBasicDetails({ deliveryMethod: e.target.value as SupplierDeliveryMethod })}
                  fullWidth
                >
                  {(Object.keys(SUPPLIER_DELIVERY_METHOD_LABELS) as SupplierDeliveryMethod[]).map((option) => (
                    <MenuItem key={option}
                      value={option}>
                      {SUPPLIER_DELIVERY_METHOD_LABELS[option]}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('expiryReviewDate', 'Expiry/review date')}
                  type="date"
                  value={basicDetails.expiryReviewDate}
                  onChange={(e) => updateBasicDetails({ expiryReviewDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
        </Section>
      )}

      {activeStep === SupplierOnboardingStep.EscalationCheck && (
        <Section>
          <Box sx={{ p: 4 }}>
            <Typography variant="textSM"
              fontWeight={600}
              sx={{ mb: 1 }}>
              {t('onboardingStepEscalationCheck', 'Full Onboarding Check')}
            </Typography>
            <Typography variant="textXS"
              color="text.secondary"
              sx={{ mb: 3 }}>
              {t('onboardingEscalationCheckDesc', 'Full onboarding (Due Diligence + Delivery & Pricing evidence) is required if any of the following apply.')}
            </Typography>

            <Stack spacing={1}>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={triggerCriteria.spendExceedsThreshold}
                    onChange={(e) => updateTriggerCriteria({ spendExceedsThreshold: e.target.checked })}
                  />
                )}
                label={t('triggerSpendExceedsThreshold', 'Annual spend with this supplier exceeds the approval threshold')}
              />
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={triggerCriteria.usedByMultipleSites}
                    onChange={(e) => updateTriggerCriteria({ usedByMultipleSites: e.target.checked })}
                  />
                )}
                label={t('triggerUsedByMultipleSites', 'Supplier is used by more than one site')}
              />
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={triggerCriteria.highRiskFoodCategory}
                    onChange={(e) => updateTriggerCriteria({ highRiskFoodCategory: e.target.checked })}
                  />
                )}
                label={t('triggerHighRiskFoodCategory', 'Supplier supplies a high-risk food category')}
              />
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={triggerCriteria.recurringOrders}
                    onChange={(e) => updateTriggerCriteria({ recurringOrders: e.target.checked })}
                  />
                )}
                label={t('triggerRecurringOrders', 'Supplier is used for recurring (not one-off) orders')}
              />
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={triggerCriteria.clientRequiresEvidence}
                    onChange={(e) => updateTriggerCriteria({ clientRequiresEvidence: e.target.checked })}
                  />
                )}
                label={t('triggerClientRequiresEvidence', 'Client contractually requires supplier due diligence evidence')}
              />
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={triggerCriteria.esgComplianceApplies}
                    onChange={(e) => updateTriggerCriteria({ esgComplianceApplies: e.target.checked })}
                  />
                )}
                label={t('triggerEsgComplianceApplies', 'ESG/compliance requirements apply to this supplier')}
              />
            </Stack>

            {fullOnboardingRequired && (
              <Alert severity="warning"
                sx={{ mt: 3 }}>
                {t('onboardingEscalationTriggeredReasons', 'Full onboarding required:')}
                <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                  {triggerReasons.map((reason) => <li key={reason}>{reason}</li>)}
                </ul>
              </Alert>
            )}
          </Box>
        </Section>
      )}

      {activeStep === SupplierOnboardingStep.DueDiligence && (
        <DueDiligenceSection profile={profile} />
      )}

      {activeStep === SupplierOnboardingStep.DeliveryAndPricing && (
        <DeliveryAndPricingSection profile={profile} />
      )}

      {activeStep === SupplierOnboardingStep.Review && (
        <Section>
          <Box sx={{ p: 4 }}>
            <Typography variant="textSM"
              fontWeight={600}
              sx={{ mb: 3 }}>
              {t('onboardingStepReview', 'Review & Approve')}
            </Typography>

            {approved && (
              <Alert severity="success"
                sx={{ mb: 3 }}>
                {t('onboardingComplete', 'Onboarding complete - {{supplierName}} has been approved.', { supplierName: basicDetails.supplierName || t('newSupplier', 'this supplier') })}
              </Alert>
            )}

            <Stack direction="row"
              alignItems="center"
              spacing={2}
              sx={{ mb: 3 }}>
              <Typography variant="textSM"
                color="text.secondary">{t('supplierType', 'Supplier type')}</Typography>
              <Chip
                label={SUPPLIER_ONBOARDING_TYPE_LABELS[effectiveType]}
                color={fullOnboardingRequired ? 'warning' : 'default'}
                size="small"
              />
              <Typography variant="textSM"
                color="text.secondary">{t('approvalStatus', 'Approval status')}</Typography>
              <Chip
                label={approved ? SupplierApprovalStatus.Approved : profile.approvalStatus}
                color={approved ? 'success' : 'warning'}
                size="small"
              />
            </Stack>

            {!approved && (
              canApprove ? (
                <Button variant="primary"
                  size="md"
                  disabled={!isBasicDetailsComplete}
                  onClick={handleComplete}>
                  {t('completeOnboarding', 'Complete onboarding (Procurement)')}
                </Button>
              ) : (
                <Typography variant="textXS"
                  color="text.secondary">
                  {t('requiresProcurementRole', 'Requires the Procurement demo role to approve.')}
                </Typography>
              )
            )}
          </Box>
        </Section>
      )}

      <Stack direction="row"
        justifyContent="space-between"
        sx={{ mt: 6 }}>
        <Button variant="secondary"
          disabled={isFirstStep}
          onClick={handleBack}>
          {t('back', 'Back')}
        </Button>
        {!isLastStep && (
          <Button
            variant="primary"
            onClick={handleNext}
            disabled={activeStep === SupplierOnboardingStep.BasicDetails && !isBasicDetailsComplete}
          >
            {t('continue', 'Continue')}
          </Button>
        )}
      </Stack>
    </PageArea>
  );
};

export default SupplierOnboardingWizard;
